import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { crmService } from '../../../services/crmService';
import { motion } from 'framer-motion';

const HeroSection = ({ onLeadSubmit }) => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    interes: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const interestOptions = [
    { value: 'compra-inmediata', label: '💎 Compra Inmediata - Listo para invertir' },
    { value: '3-6-meses', label: '📅 En 3-6 meses - Planificando inversión' },
    { value: 'solo-informacion', label: '📊 Solo Información - Explorando opciones' },
    { value: 'inversion', label: '🚀 Inversión - Busco rentabilidad' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.nombre?.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData?.nombre?.trim()?.length < 3) {
      newErrors.nombre = 'El nombre debe tener al menos 3 caracteres';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Email inválido. Verifica el formato';
    }

    if (!formData?.telefono?.trim()) {
      newErrors.telefono = 'El teléfono es requerido';
    } else if (!/^[0-9]{9}$/?.test(formData?.telefono?.replace(/\s/g, ''))) {
      newErrors.telefono = 'Teléfono debe tener 9 dígitos';
    }

    if (!formData?.interes) {
      newErrors.interes = 'Seleccione su nivel de interés';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // ✅ Payload para Edge Function
      const leadPayload = {
        nombre: formData?.nombre?.trim(),
        email: formData?.email?.trim(),
        telefono: formData?.telefono?.replace(/\s/g, ''),
        timeframe: formData?.interes,
        fuente: 'website',
        mensaje: '', // si luego agregas textarea, aquí va
        presupuesto: null,
        proyectoInteres: 'Residencial 3 Volcanes'
      };

      // ✅ Extras (source_url + utm + campaign) para metadata de notifications
      const url = window.location?.href;
      const params = new URLSearchParams(window.location.search);

      const extras = {
        sourceUrl: url,
        utm_source: params?.get('utm_source'),
        utm_medium: params?.get('utm_medium'),
        utm_campaign: params?.get('utm_campaign'),
        utm_term: params?.get('utm_term'),
        utm_content: params?.get('utm_content'),
        campaign: params?.get('campaign'),
        page: 'HeroSection'
      };

      const result = await crmService?.createLeadWithAI(leadPayload, null, extras);

      console.log('qualify-lead result:', result);

      // ✅ callback externo (si lo usas para analytics/UI)
      onLeadSubmit?.(formData);

      // ✅ reset
      setFormData({ nombre: '', email: '', telefono: '', interes: '' });
      setErrors({});
    } catch (error) {
      console.error('Error submitting lead:', error);
      setErrors({
        submit: error?.message || 'Error al enviar el formulario. Inténtalo nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 pb-12 sm:pb-16 bg-gradient-to-br from-orange-50 via-white to-blue-50">
      {/* Background with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 z-10" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(234,88,12,0.08),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(30,64,175,0.06),transparent_50%)]" />
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
          {/* Left Content - Enhanced Sales Copy */}
          <motion.div
            className="space-y-6 sm:space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Trust Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 text-white border-2 border-emerald-300 rounded-full shadow-lg"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, type: 'spring' }}
            >
              <Icon name="Award" size={20} className="text-white flex-shrink-0" />
              <span className="text-xs sm:text-sm font-black tracking-wide">✓ Proyecto Certificado 2026</span>
            </motion.div>

            {/* Main Headline */}
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-heading font-black leading-[1.1] tracking-tight">
                <span className="bg-gradient-to-r from-orange-600 via-red-600 to-orange-500 bg-clip-text text-transparent drop-shadow-sm">
                  Invierte en tu Futuro
                </span>
                <br />
                <span className="text-gray-900">Hoy Mismo</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 font-semibold leading-relaxed">
                Lotes premium en <span className="text-orange-600 font-black">Arequipa</span> con
                <span className="text-emerald-600 font-black"> plusvalía garantizada del +35% anual</span>.
                Tu patrimonio comienza aquí.
              </p>
            </div>

            {/* Key Benefits - Mobile optimized grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: 'TrendingUp', text: 'Plusvalía +35% anual', color: 'from-emerald-500 to-green-600', textColor: 'text-emerald-700' },
                { icon: 'DollarSign', text: 'Desde S/ 15,000', color: 'from-orange-500 to-red-600', textColor: 'text-orange-700' },
                { icon: 'Calendar', text: 'Financiamiento 5 años', color: 'from-blue-500 to-indigo-600', textColor: 'text-blue-700' },
                { icon: 'MapPin', text: 'Ubicación estratégica', color: 'from-amber-500 to-orange-600', textColor: 'text-amber-700' }
              ]?.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 lg:p-5 rounded-xl sm:rounded-2xl bg-white border-2 border-gray-200 hover:border-orange-400 hover:shadow-xl transition-all"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -4 }}
                >
                  <div className={`p-2 sm:p-3 rounded-xl bg-gradient-to-br ${benefit?.color} shadow-lg flex-shrink-0`}>
                    <Icon name={benefit?.icon} size={18} className="text-white" />
                  </div>
                  <span className={`font-bold text-xs sm:text-sm lg:text-base ${benefit?.textColor}`}>{benefit?.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Social Proof - Mobile optimized */}
            <motion.div
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-orange-50 rounded-xl sm:rounded-2xl border-2 border-gray-200"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="flex -space-x-2 sm:-space-x-3">
                  {[1, 8, 11, 4]?.map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 border-2 sm:border-3 border-white flex items-center justify-center text-white font-bold shadow-lg text-sm"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm sm:text-base font-black text-gray-900">+150 familias</p>
                  <p className="text-xs sm:text-sm text-gray-600 font-semibold">Ya invirtieron</p>
                </div>
              </div>
              <div className="h-px sm:h-12 w-full sm:w-px bg-gray-300" />
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5]?.map((i) => (
                    <Icon key={i} name="Star" size={14} className="text-amber-500 fill-amber-500" />
                  ))}
                </div>
                <p className="text-xs sm:text-sm text-gray-700 font-bold">4.9/5 en satisfacción</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - Enhanced Lead Form - Mobile optimized */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full max-w-md mx-auto lg:max-w-none"
          >
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl border-2 border-orange-200 relative overflow-hidden">
              {/* Form Header */}
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-orange-500 via-red-500 to-orange-600" />

              <div className="p-5 sm:p-6 lg:p-8 space-y-4 sm:space-y-5 lg:space-y-6">
                <div className="text-center space-y-2 sm:space-y-3">
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 lg:px-5 py-1.5 sm:py-2 bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-full mb-2">
                    <Icon name="Gift" size={18} className="text-emerald-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-black text-emerald-700">Asesoría Gratuita</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl font-heading font-black text-gray-900">Reserva tu Lote Hoy</h3>
                  <p className="text-sm sm:text-base text-gray-600 font-semibold">
                    Completa el formulario y recibe atención personalizada en menos de 2 horas
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                  <div>
                    <Input
                      type="text"
                      placeholder="Nombre completo *"
                      value={formData?.nombre}
                      onChange={(e) => handleInputChange('nombre', e?.target?.value)}
                      error={errors?.nombre}
                      leftIcon={<Icon name="User" size={20} />}
                      className="h-14 text-base border-2 border-gray-300 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <Input
                      type="email"
                      placeholder="Email *"
                      value={formData?.email}
                      onChange={(e) => handleInputChange('email', e?.target?.value)}
                      error={errors?.email}
                      leftIcon={<Icon name="Mail" size={20} />}
                      className="h-14 text-base border-2 border-gray-300 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <Input
                      type="tel"
                      placeholder="Teléfono (9 dígitos) *"
                      value={formData?.telefono}
                      onChange={(e) => handleInputChange('telefono', e?.target?.value)}
                      error={errors?.telefono}
                      leftIcon={<Icon name="Phone" size={20} />}
                      className="h-14 text-base border-2 border-gray-300 focus:border-orange-500"
                    />
                  </div>

                  <div>
                    <Select
                      options={interestOptions}
                      value={formData?.interes}
                      onChange={(value) => handleInputChange('interes', value)}
                      placeholder="¿Cuándo planeas invertir? *"
                      error={errors?.interes}
                    />
                  </div>

                  {errors?.submit && (
                    <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30 flex items-start gap-3">
                      <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-destructive">{errors?.submit}</p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    fullWidth
                    disabled={isSubmitting}
                    className="h-14 text-lg font-bold shadow-xl hover:shadow-2xl transition-all"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="Loader2" size={24} className="mr-2 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Icon name="Send" size={24} className="mr-2" />
                        Quiero mi Asesoría Gratis
                      </>
                    )}
                  </Button>
                </form>

                {/* Trust Indicators */}
                <div className="pt-6 border-t border-border space-y-3">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Icon name="Lock" size={16} className="text-success" />
                    <span>Tus datos están 100% protegidos</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Icon name="Clock" size={16} className="text-primary" />
                    <span>Respuesta garantizada en menos de 2 horas</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span>Sin compromiso de compra</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Limited Time Offer */}
            <motion.div
              className="mt-6 p-4 rounded-xl bg-gradient-to-r from-warning/20 to-warning/10 border-2 border-warning/30"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="flex items-center gap-3">
                <Icon name="Zap" size={24} className="text-warning" />
                <div>
                  <p className="font-bold text-foreground">🎁 Bono Especial</p>
                  <p className="text-sm text-muted-foreground">
                    Separa hoy y recibe <span className="text-warning font-bold">asesoría legal gratuita</span>
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm text-muted-foreground font-medium">Descubre más</p>
          <Icon name="ChevronDown" size={24} className="text-primary" />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
